import React, { memo, useCallback, useEffect, useMemo } from 'react';

import { createUseStyles, Checkbox, Text } from '@Sage/hoi-poi-ui';
import { cleanString } from '@Sage/ssm-utils';
import Fuse from 'fuse.js';

import styles from '../styles';
const useStyles = createUseStyles(styles, 'ModalOptions');

const fuseOptions = {
    threshold: 0.2,
    keys: ['label'],
    getFn: (obj, path) => {
        const value = Fuse.config.getFn(obj, path);
        return cleanString(value);
    },
};

const OptionGroup = memo(
    ({ disabled, onSelect, schema, search, sectionId = false, selected, setContentLength }) => {
        const classes = useStyles();
        const renderOption = useCallback(
            (option, options, index, parentId, isChecked, isDisabled) => {
                return (
                    <div key={`${option.id}-${index}`} className={classes.option}>
                        <Checkbox
                            onChange={onSelect({
                                id: option.id,
                                parentId,
                                options,
                                sectionId: sectionId,
                            })}
                            checked={isChecked}
                            isDisabled={isDisabled}
                            color="actionMinor"
                        />
                        <Text className={classes.optionText} type="body">
                            {option.label}
                        </Text>
                        {option.comment && (
                            <Text className={classes.optionComment} type="body">
                                {option.comment}
                            </Text>
                        )}
                    </div>
                );
            },
            [classes.option, classes.optionComment, classes.optionText, onSelect, sectionId],
        );

        const finalOptions = useCallback(
            ({ options, parentId, parentLabel }) => {
                if (!options || options.length === 0) return null;

                const finalSearch = search ? cleanString(search.toLowerCase()) : null;

                const allItems = [
                    { id: parentId, label: cleanString(parentLabel.toLowerCase()) },
                    ...options,
                ];
                const fuse = new Fuse(allItems, fuseOptions);

                const matchingOptions = finalSearch
                    ? fuse.search(finalSearch).map((result) => result.item.id)
                    : [];

                const matchesParent = matchingOptions.includes(parentId);

                const shouldRenderOption = (option) => {
                    if (!finalSearch) return true;
                    if (matchingOptions.length === 0) return false;
                    if (matchesParent && matchingOptions.length === 1) return true;
                    return matchingOptions.includes(option.id);
                };

                const finalOptions = options.reduce((arr, option, index) => {
                    if (!option.label || !option.id) return arr;

                    const isChecked = selected?.[parentId]?.options.includes(option.id) || false;
                    const isDisabled =
                        disabled?.[parentId]?.options.includes(option.id) ||
                        option.forceDisabled ||
                        false;

                    if (shouldRenderOption(option)) {
                        arr.push(
                            renderOption(option, options, index, parentId, isChecked, isDisabled),
                        );
                    }

                    return arr;
                }, []);

                return finalOptions;
            },
            [search, selected, disabled, renderOption],
        );

        const optionGroupContent = useMemo(
            () =>
                schema.reduce((arr, group, index) => {
                    if (!group.id || !group.options) return arr;
                    const renderOptions =
                        (group?.options?.length > 0 &&
                            finalOptions({
                                options: group.options || [],
                                parentId: group.id,
                                parentLabel: group.label,
                            })) ||
                        [];

                    if (renderOptions.length === 0) return arr;

                    const enabledOptions =
                        group.options.filter(
                            (option) => !disabled?.[group.id]?.options.includes(option.id),
                        ) || [];
                    const enabledCheckedOptions =
                        selected?.[group.id]?.options.filter(
                            (option) => !disabled?.[group.id]?.options.includes(option),
                        ) || [];
                    const isIndeterminate =
                        (!selected?.[group.id]?.isSelected &&
                            enabledCheckedOptions.length > 0 &&
                            enabledOptions.length !== enabledCheckedOptions.length) ||
                        false;
                    const isChecked =
                        selected?.[group.id]?.isSelected ||
                        enabledOptions.length === enabledCheckedOptions.length ||
                        false;
                    const isDisabled = disabled?.[group.id]?.isDisabled;

                    arr.push(
                        <div key={`${group.id}-${index}`} className={classes.group}>
                            <div className={classes.groupTitle}>
                                <Checkbox
                                    onChange={onSelect({
                                        id: group.id,
                                        options: group.options,
                                        sectionId: sectionId,
                                    })}
                                    checked={isChecked || isIndeterminate}
                                    indeterminate={isIndeterminate}
                                    isDisabled={isDisabled}
                                    color="actionMinor"
                                />
                                <Text className={classes.optionText} type="subtitle" medium>
                                    {group.label}
                                </Text>
                            </div>
                            <div className={classes.optionContainer}>{renderOptions}</div>
                        </div>,
                    );

                    return arr;
                }, []),
            [
                classes.group,
                classes.groupTitle,
                classes.optionContainer,
                classes.optionText,
                disabled,
                finalOptions,
                onSelect,
                schema,
                sectionId,
                selected,
            ],
        );

        useEffect(() => {
            setContentLength && setContentLength(optionGroupContent.length);
        }, [optionGroupContent, setContentLength]);

        return optionGroupContent;
    },
);

export default OptionGroup;
